import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Download } from 'lucide-react';
import { ScrollReveal } from './shared';

const EvidenceSection = () => {
  const scrollRef = useRef(null);
  const sectionRef = useRef(null);
  const rafRef = useRef(null);
  const pausedRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);

  const cases = [
    {
      title: 'National Auto Service Growth Program',
      subtitle: 'National Multi-site Operator',
      metrics: ['$100M revenue impact', '+2.5% YoY growth in pilot stores', '-2.8% in non-pilot stores', '270 stores transformed'],
    },
    {
      title: 'Medical Device Market Repositioning',
      subtitle: 'Leading Medical Device Company',
      metrics: ['85% brand loyalty retained', '3x reimbursement approval rate', '18-month accelerated market entry', '$240M first-year revenue target'],
    },
    {
      title: 'Wealth Management Retention Analytics',
      subtitle: 'Large Financial Services Firm',
      metrics: ['8% reduction in policy lapse rate', '~5% acceleration in retention and cross-sell activity'],
    },
    {
      title: 'Customer Engagement Prioritization',
      subtitle: 'Global Dental Technology Company',
      metrics: ['Top 5 initiatives could affect ~70% of target customers', '~32% would pay 10%+ more', '~11% upside in consumables, ~6% equipment'],
    },
    {
      title: 'Pharmaceutical Product Launch Intelligence',
      subtitle: 'Global Healthcare Leader',
      metrics: ['10-country research across 1,000+ physicians', 'Patient share within 1% of secondary data', 'Optimal launch date identified', 'Choice drivers mapped across 31 endpoints'],
    },
    {
      title: 'Patient Adherence Behavioral Analysis',
      subtitle: 'Pharmaceutical & Payer Organization',
      metrics: ['1M+ patient insights analyzed', 'Kitchen-table decision points identified', 'Behavioral levers mapped for adherence'],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const tick = useCallback(() => {
    const el = scrollRef.current;
    if (el && !pausedRef.current) {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll > 0) {
        if (el.scrollLeft >= maxScroll - 1) {
          el.scrollLeft = 0;
        } else {
          el.scrollLeft += 0.6;
        }
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (isVisible) {
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isVisible, tick]);

  const handleMouseEnter = () => { pausedRef.current = true; };
  const handleMouseLeave = () => { pausedRef.current = false; };
  const handleTouchStart = () => { pausedRef.current = true; };
  const handleTouchEnd = () => { setTimeout(() => { pausedRef.current = false; }, 3000); };

  return (
    <section id="evidence" className="section-spacing bg-white" data-testid="evidence-section" ref={sectionRef}>
      <div className="container-luxury">
        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="overline mb-4">Case Evidence</p>
            <h2 className="heading-section text-4xl sm:text-5xl text-[#0B1A2B] mb-6" data-testid="evidence-headline">
              Documented outcomes from high-stakes growth decisions.
            </h2>
          </div>
        </ScrollReveal>

        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-6 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            data-testid="evidence-scroll"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {cases.map((c, index) => (
              <div key={index} className="min-w-[340px] sm:min-w-[400px] max-w-[420px] flex-shrink-0 p-8 bg-white border border-[#E2E8F0] transition-all duration-500 hover:shadow-lg hover:border-[#D97706]/30" data-testid={`evidence-card-${index + 1}`}>
                <p className="text-xs text-[#D97706] uppercase tracking-wider font-medium mb-2">{c.subtitle}</p>
                <h3 className="font-serif text-xl text-[#0B1A2B] mb-6 leading-tight">{c.title}</h3>
                <ul className="space-y-3">
                  {c.metrics.map((metric, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-[#D97706] text-sm mt-0.5 shrink-0">—</span>
                      <span className="text-[#334155] text-[0.9375rem] leading-relaxed">{metric}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center mt-12 mb-4">
            <p className="font-serif text-lg text-[#475569] italic">
              The board approves on evidence. The initiative is funded, owned, and moving. Growth shows up within quarters.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/evidence"
              className="inline-flex items-center text-sm font-medium text-[#0B1A2B] hover:text-[#D97706] transition-colors duration-300"
              data-testid="view-all-evidence-btn"
            >
              View all case evidence
              <span className="ml-2">→</span>
            </a>
            <span className="hidden sm:inline text-[#E2E8F0]">|</span>
            <a
              href="/evidence"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#D97706] hover:text-[#B45309] transition-colors duration-300"
              data-testid="download-evidence-cta"
            >
              <Download className="w-3.5 h-3.5" />
              Download individual case studies as PDF
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default EvidenceSection;
